/**
 * Size model for the floating desktop Astra panel: the bottom-right anchor,
 * the drag limits and the normal/maximized footprints. Pure functions over
 * the viewport so the frame and its tests share one source of truth.
 *
 * Everything here is structural (panel dimensions and viewport fractions)
 * with no design-token equivalent, hence the literals.
 */
export interface PanelSize {
  readonly width: number;
  readonly height: number;
}

/** Minimum drag size (px); the max is capped by the frame's CSS. */
export const MIN_WIDTH = 320;
export const MIN_HEIGHT = 360;

// Default/maximized sizes as viewport fractions with px caps. re-resizable is
// size-controlled, so these seed its numeric size.
const DEFAULT_WIDTH_CAP = 480;
const DEFAULT_WIDTH_FRACTION = 0.35;
const DEFAULT_HEIGHT_FRACTION = 0.7;
const MAX_WIDTH_CAP = 920;
const MAX_WIDTH_FRACTION = 0.92;
const MAX_HEIGHT_FRACTION = 0.88;

/** Viewport fraction the panel's bottom edge is anchored at (`bottom: 5%`). */
export const BOTTOM_ANCHOR_FRACTION = 0.95;
/** Fixed px of top chrome (TopNavigation + AppLayout toolbar) the panel must clear. */
export const TOP_CHROME_PX = 96;

function viewportWidth(): number {
  return typeof window === 'undefined' ? 1024 : window.innerWidth;
}

function viewportHeight(): number {
  return typeof window === 'undefined' ? 768 : window.innerHeight;
}

/** Tallest height the frame's CSS ceiling allows: MIN_HEIGHT, or the anchored viewport minus the top chrome. */
export function maxAvailableHeight(): number {
  return Math.max(
    MIN_HEIGHT,
    Math.round(viewportHeight() * BOTTOM_ANCHOR_FRACTION) - TOP_CHROME_PX,
  );
}

/** Default normal-mode size, seeded from the viewport. */
export function defaultNormalSize(): PanelSize {
  return {
    width: Math.min(
      DEFAULT_WIDTH_CAP,
      Math.round(viewportWidth() * DEFAULT_WIDTH_FRACTION),
    ),
    height: Math.min(
      Math.round(viewportHeight() * DEFAULT_HEIGHT_FRACTION),
      maxAvailableHeight(),
    ),
  };
}

/** Fixed (not full-screen) size used by the expand control. */
export function maximizedSize(): PanelSize {
  return {
    width: Math.min(MAX_WIDTH_CAP, Math.round(viewportWidth() * MAX_WIDTH_FRACTION)),
    height: Math.min(
      Math.round(viewportHeight() * MAX_HEIGHT_FRACTION),
      maxAvailableHeight(),
    ),
  };
}
